import { UpgradeSubscriptionModal } from '@components'

import { Link, SectionMessage, Space, Text, useModal, withModalConditionalRender } from '@billy/design-system'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { ModalId } from '../../../../enums/modalId'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useNextVoucherNumber } from '../../../bills/elements/BillEditableForm/hooks/useNextVoucherNumber'
import { BankTransactionDrawerProvider, BookkeepingItemsProvider, useSandboxModeContext } from './contexts'
import { BankTransactionDrawer } from './elements/BankTransactionDrawer'
import { BankTransactionsFilters } from './elements/BankTransactionsFilters'
import { BankTransactionsList } from './elements/BankTransactionsList'
import { CreateManualPaymentModal as CreateManualPaymentModalComponent } from './elements/CreateManualPaymentModal'
import * as Styled from './styles'

const CEGID_ADVISOR_LEARN_MORE_URL =
  'https://help.shine.fr/shine-facture/fr/articles/17672893-rapprochement-bancaire-et-cegid-loop'

const ConditionalCreateManualPaymentModal = withModalConditionalRender(CreateManualPaymentModalComponent)

interface CreateManualPaymentModalProps {
  transaction: BankLine | null
}

const CreateManualPaymentModal = ({ transaction }: CreateManualPaymentModalProps): ReactElement => {
  const { nextVoucherNumber } = useNextVoucherNumber()

  return (
    <ConditionalCreateManualPaymentModal
      id={ModalId.CreateManualPaymentModal}
      nextVoucherNumber={nextVoucherNumber}
      transaction={transaction}
    />
  )
}

interface BankTransactionsContentProps {
  /**
   * Passed through to BankTransactionDrawer — see its own prop doc. Omitted on the real route.
   */
  drawerPortalContainer?: Element
}

/**
 * Shared inner tree for the Bank Transactions experience — filters, list, drawer and modals,
 * with no page-level chrome (ModuleLayout). Rendered both by the real routed BankTransactions
 * page (wrapped in ModuleLayout there) and by the sandbox pane (no ModuleLayout, its own chrome).
 */
export const BankTransactionsContent = ({ drawerPortalContainer }: BankTransactionsContentProps): ReactElement => {
  const { t } = useTranslation()
  const isSandboxMode = !!useSandboxModeContext()
  const [transactionForModal, setTransactionForModal] = useState<BankLine | null>(null)
  const { open: openModal } = useModal(ModalId.CreateManualPaymentModal)
  const { organization } = useUserOrganization()

  const isCegidOrganization = organization?.isCegid

  const handleOpenCreateManualPaymentModal = useCallback(
    (transaction: BankLine) => {
      setTransactionForModal(transaction)
      openModal()
    },
    [openModal],
  )

  return (
    <BookkeepingItemsProvider>
      <BankTransactionDrawerProvider>
        {isCegidOrganization && (
          <>
            <SectionMessage variant="info">
              {t('bank_transactions.cegid_banner.message')}{' '}
              <Text as="span" inherit weight="bold">
                <Link href={CEGID_ADVISOR_LEARN_MORE_URL} rel="noopener noreferrer" target="_blank">
                  {t('bank_transactions.cegid_banner.learn_more')}
                </Link>
              </Text>
            </SectionMessage>
            <Space size="s24" />
          </>
        )}
        <Styled.HiddenTitle>{t('navigation.menu.label.banking.bank_transactions')}</Styled.HiddenTitle>
        <BankTransactionsFilters />
        <BankTransactionsList />
        <BankTransactionDrawer
          onCreateManualPaymentClick={handleOpenCreateManualPaymentModal}
          portalContainer={drawerPortalContainer}
        />
        {!isSandboxMode && <CreateManualPaymentModal key={transactionForModal?.id} transaction={transactionForModal} />}
        <UpgradeSubscriptionModal
          id={ModalId.UpgradeSubscriptionModal}
          trackingContext={TrackingContext.BankTransactions}
        />
      </BankTransactionDrawerProvider>
    </BookkeepingItemsProvider>
  )
}
